<script lang="ts">
  import logo from '$lib/assets/quilty-primary-logo.png';
  import { VERTICALS, DEFAULT_VERTICAL, type Vertical } from '$lib/brand/verticals';

  interface Props {
    /** Which product line the vertical name identifies. */
    vertical?: Vertical;
    /** Lockup size. `sm` is for compact nav bars, `md` for marketing surfaces. */
    size?: 'sm' | 'md';
    /** `horizontal` for nav bars; `stacked` matches the brand mockup. */
    orientation?: 'horizontal' | 'stacked';
    /** Divider/contrast tone. Use `dark` on dark backgrounds (e.g. footer). */
    tone?: 'light' | 'dark';
    /** Link target. Pass `null` to render without an anchor. */
    href?: string | null;
    /** Drop the vertical name (e.g. at very small sizes) leaving only the mark. */
    showVertical?: boolean;
    class?: string;
  }

  const {
    vertical = DEFAULT_VERTICAL,
    size = 'md',
    orientation = 'horizontal',
    tone = 'light',
    href = '/',
    showVertical = true,
    class: className = '',
  }: Props = $props();

  const spec = $derived(VERTICALS[vertical]);

  const logoWidth = $derived(size === 'sm' ? 140 : 160);
  const nameSizeClass = $derived(size === 'sm' ? 'text-[11px]' : 'text-xs');
  const dividerColorClass = $derived(tone === 'dark' ? 'bg-white/40' : 'bg-zinc-300');
</script>

{#snippet lockup()}
  {#if orientation === 'stacked'}
    <span class="inline-flex flex-col items-center gap-2">
      <img
        src={logo}
        width={logoWidth}
        alt="Quilty"
        class="h-auto"
        style="width:{logoWidth}px"
      />
      {#if showVertical}
        <span class="h-px w-16 {dividerColorClass}" aria-hidden="true"></span>
        <span
          class="font-semibold uppercase tracking-[0.25em] {nameSizeClass}"
          style="color:{spec.color}"
        >{spec.label}</span>
      {/if}
    </span>
  {:else}
    <span class="inline-flex items-center gap-3">
      <img
        src={logo}
        width={logoWidth}
        alt="Quilty"
        class="h-auto"
        style="width:{logoWidth}px"
      />
      {#if showVertical}
        <span class="h-6 w-px {dividerColorClass}" aria-hidden="true"></span>
        <span
          class="font-semibold uppercase tracking-[0.2em] whitespace-nowrap {nameSizeClass}"
          style="color:{spec.color}"
        >{spec.label}</span>
      {/if}
    </span>
  {/if}
{/snippet}

{#if href}
  <!-- href is an arbitrary prop (often a bare path), so it can't be passed to resolve(). -->
  <!-- eslint-disable-next-line svelte/no-navigation-without-resolve -->
  <a {href} class="inline-flex items-center shrink-0 {className}" aria-label="Quilty {spec.label}">
    {@render lockup()}
  </a>
{:else}
  <span class="inline-flex items-center shrink-0 {className}">
    {@render lockup()}
  </span>
{/if}
